import {Map, List, isList} from 'immutable';
import LocalStorageConstants from '~/constants/LocalStorageConstants';
import {getProductState} from '~/selectors/ProductSelectors';
import {getQuoteState} from '~/selectors/QuoteSelectors';
import {getNamesAndNumbersJS} from '~/selectors/NamesAndNumbersSelectors';
import {getFundraisingState} from '~/selectors/FundraisingSelectors';
import {getCanvas} from '~/selectors/CanvasSelectors';

const {MAX_DESIGNS, MAX_ICONS} = LocalStorageConstants;

const getRequiredDesignIds = data =>
  List([getCanvas(data).get('currentDesignId')]);

const getTransientDesignCount = data =>
  Math.max(0, MAX_DESIGNS - getRequiredDesignIds(data).size);

const getTransientDesignIds = data => {
  const requiredDesignIds = getRequiredDesignIds(data);
  const transientDesignCount = getTransientDesignCount(data);
  return transientDesignCount
    ? data.design.present
        .keySeq()
        .filterNot(designId => requiredDesignIds.includes(designId))
        .take(transientDesignCount)
    : List();
};

const getRequiredDesigns = data =>
  getRequiredDesignIds(data).reduce(
    (acc, designId) => acc.set(designId, data.design.present.get(designId)),
    Map()
  );

const getTransientDesigns = data =>
  getTransientDesignIds(data).reduce(
    (acc, designId) => acc.set(designId, data.design.present.get(designId)),
    Map()
  );

export const serializeCanvas = data => {
  const canvasData = getCanvas(data).toObject();
  return {
    ...canvasData,
    selectedIconIds: []
  };
};

export const serializeDesigns = data => {
  // The current design on the canvs must be saved
  // In the future a user may have multiple "current" designs
  const requiredDesigns = getRequiredDesigns(data);
  // Any additional designs may be saved for convenience, up to the maximum
  const transientDesigns = getTransientDesigns(data);
  return requiredDesigns.merge(transientDesigns).toJS();
};

export const serializeFundraising = data => getFundraisingState(data).toJS();

export const serializeIcon = icon => {
  let iconData = icon;

  // Ensure colors is an Immutable List
  if (iconData.colors && !isList(iconData.colors)) {
    iconData = iconData.set('colors', List(iconData.colors));
  }
  if (iconData.spotColors && !isList(iconData.spotColors)) {
    iconData = iconData.set('spotColors', List(iconData.spotColors));
  }

  iconData = iconData.toJS();

  if (iconData.color) {
    const {r, g, b} = iconData.color;
    delete iconData.color;
    iconData.fgcolor = [r, g, b];
  }

  if (iconData.outlineColor) {
    const {r, g, b} = iconData.outlineColor;
    delete iconData.outlineColor;
    iconData.outline = {
      color: [r, g, b],
      width: iconData.outlineWidth,
      clearFill: iconData.outlineClearFill
    };
  }

  if (iconData.effectSetting) {
    iconData.effect = {
      type: iconData.effectType,
      setting: iconData.effectSetting
    };
  }

  return iconData;
};

export const serializeIcons = data => {
  const icons = data.icon.present;

  const requiredIconIds = getRequiredDesigns(data)
    .merge(getTransientDesigns(data))
    .reduce((acc, design) => acc.merge(design.get('iconIds')), List())
    .merge(getCanvas(data).get('recentUploadIconIds'));
  const transientIconCount = Math.max(0, MAX_ICONS - requiredIconIds.size);

  // Any icons used in local designs must be saved for integrity
  const requiredIcons = icons.filter(icon => requiredIconIds.includes(icon.id));

  // Any additional icons may be saved for convenience, up to the maximum
  const transientIcons = transientIconCount
    ? icons
        .filterNot(icon => requiredIconIds.includes(icon.id))
        .take(transientIconCount)
    : Map();

  return requiredIcons.merge(transientIcons).map(serializeIcon).toJS();
};

export const serializeNamesAndNumbers = data => getNamesAndNumbersJS(data);

export const serializeProduct = data => getProductState(data).toJS();
export const serializeQuote = data => getQuoteState(data).toJS();

export const serialize = data => {
  return {
    canvas: serializeCanvas(data),
    design: serializeDesigns(data),
    icon: serializeIcons(data),
    product: serializeProduct(data),
    namesAndNumbers: serializeNamesAndNumbers(data),
    quote: serializeQuote(data),
    fundraising: serializeFundraising(data)
  };
};
